import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { Empresa } from "../../types/empresa";
import { EmpresaCreate, EmpresaUpdate } from "../../types/empresa";
import {
  ufService,
  municipioService,
  bairroService,
} from "../../services/enderecoService";
import { UF, Option } from "../../types/localidade"; // Importar o tipo Option e UF de localidade
import { useAuth } from "../../contexts/AuthContext"; // Para verificar permissões
import { planoService } from "../../services/planoService";
import { Plano, AcessoChoiceLabels } from "../../types/plano";

interface AssinanteFormProps {
  initialData?: Partial<Empresa>;
  onSubmit: (data: EmpresaCreate | Partial<EmpresaUpdate>) => void;
  isEdit?: boolean;
  loading?: boolean;
  onPlanSelected: (planoId: number | null) => void; // Adicionado
}

export const AssinanteForm: React.FC<AssinanteFormProps> = ({
  initialData,
  onSubmit,
  isEdit = false,
  loading = false,
  onPlanSelected,
}) => {
  // Verifica tipo de usuário para permissões
  const { user } = useAuth();
  const isTactUser = user?.tipo === "TACT";

  // Ref para armazenar os valores iniciais e comparar mudanças
  const initialValuesRef = useRef<any>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<EmpresaCreate | EmpresaUpdate>({
    // Os valores padrão para o endereço agora serão tratados pelos estados dos seletores
    defaultValues: {
      tipo: initialData?.tipo || "PJ",
      razao_social: initialData?.razao_social || "",
      nome_fantasia: initialData?.nome_fantasia || "",
      cnpj: initialData?.cnpj || "",
      inscricao_municipal: initialData?.inscricao_municipal || "",
      inscricao_estadual: initialData?.inscricao_estadual || "",
      admin_email:
        initialData?.membros?.find((m) => m.cargo === "ADMIN")?.user.email ??
        "",
      admin_nome_completo:
        initialData?.membros?.find((m) => m.cargo === "ADMIN")?.user
          .nome_completo ?? "",
      admin_telefone: "", // Telefone não vem na listagem, então fica em branco na edição
      endereco: {
        zipcode: initialData?.endereco?.logradouro.zipcode || "",
        nome: initialData?.endereco?.logradouro.nome || "",
        numero: initialData?.endereco?.numero || 0,
        complemento: initialData?.endereco?.complemento || "",
        uf_sigla: initialData?.endereco?.logradouro.cidade.uf?.sigla || "", // Acesso seguro
        cidade_nome: initialData?.endereco?.logradouro.cidade?.nome || "", // Acesso seguro
        bairro_nome: initialData?.endereco?.logradouro.bairro.nome || "",
      },
    },
  });

  // Estados para Planos
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
  const [selectedPlanoId, setSelectedPlanoId] = useState<number | null>(
    initialData?.plano?.id || null
  );

  // Efeito para carregar planos
  useEffect(() => {
    const fetchPlanos = async () => {
      try {
        setLoadingPlans(true);
        const response = await planoService.listar(1, 100); // Buscar até 100 planos
        setPlanos(response.results);
      } catch (error) {
        console.error("Erro ao buscar planos:", error);
        setErrorMessage("Erro ao carregar planos. Tente novamente.");
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlanos();
  }, []);

  // Notificar o pai sobre o plano selecionado
  useEffect(() => {
    onPlanSelected(selectedPlanoId);
  }, [selectedPlanoId, onPlanSelected]);

  // Armazena os valores iniciais quando em modo de edição
  useEffect(() => {
    if (isEdit && initialData && !initialValuesRef.current) {
      initialValuesRef.current = {
        tipo: initialData?.tipo || "PJ",
        razao_social: initialData?.razao_social || "",
        nome_fantasia: initialData?.nome_fantasia || "",
        cnpj: initialData?.cnpj || "",
        inscricao_municipal: initialData?.inscricao_municipal || "",
        inscricao_estadual: initialData?.inscricao_estadual || "",
        admin_email:
          initialData?.membros?.find((m) => m.cargo === "ADMIN")?.user.email ??
          "",
        admin_nome_completo:
          initialData?.membros?.find((m) => m.cargo === "ADMIN")?.user
            .nome_completo ?? "",
        admin_telefone: "", // Telefone não vem na listagem, então fica em branco na edição
        endereco: {
          zipcode: initialData?.endereco?.logradouro.zipcode || "",
          nome: initialData?.endereco?.logradouro.nome || "",
          numero: initialData?.endereco?.numero || 0,
          complemento: initialData?.endereco?.complemento || "",
          uf_sigla: initialData?.endereco?.logradouro.cidade.uf?.sigla || "",
          cidade_nome: initialData?.endereco?.logradouro.cidade?.nome || "",
          bairro_nome: initialData?.endereco?.logradouro.bairro.nome || "",
        },
      };
    }
  }, [isEdit, initialData]);

  // Estados para os seletores em cascata
  const [ufs, setUfs] = useState<UF[]>([]);
  const [cidades, setCidades] = useState<Option[]>([]);
  const [bairros, setBairros] = useState<Option[]>([]);
  
  // Estado para controlar se está editando endereço (modo de edição)
  const [isEditingEndereco, setIsEditingEndereco] = useState(false);

  // Estado para mensagem de erro/validação
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Ref para scroll automático ao erro
  const errorBannerRef = useRef<HTMLDivElement>(null);

  const [selectedUfId, setSelectedUfId] = useState<number | null>(
    initialData?.endereco?.logradouro.cidade.uf?.id || null
  );
  const [selectedCidade, setSelectedCidade] = useState<number | null>(
    initialData?.endereco?.logradouro.cidade?.id || null
  );
  const [selectedBairro, setSelectedBairro] = useState<number | null>(
    initialData?.endereco?.logradouro.bairro?.id || null
  );


  // Carregar UFs na montagem do componente
  useEffect(() => {
    ufService.listar().then((data: UF[]) => {
      setUfs(data);
    });
  }, []);

  // Carregar até 5000 cidades ao selecionar UF
  useEffect(() => {
    if (selectedUfId) {
      municipioService.listarPorUF(String(selectedUfId)).then((options) => {
        setCidades(options);
        if (selectedCidade && !options.some((opt) => Number(opt.value) === selectedCidade)) {
          setSelectedCidade(null);
          setValue("endereco.cidade_nome", "");
          setSelectedBairro(null);
          setValue("endereco.bairro_nome", "");
        }
        setBairros([]);
      });
    } else {
      setCidades([]);
      setBairros([]);
      setSelectedCidade(null);
      setValue("endereco.cidade_nome", "");
      setSelectedBairro(null);
      setValue("endereco.bairro_nome", "");
    }
  }, [selectedUfId, setValue]);

  // Carregar Bairros quando a Cidade muda
  useEffect(() => {
    if (selectedCidade) {
      bairroService.listarPorMunicipio(selectedCidade).then((data: Option[]) => {
        setBairros(data);
        if (selectedBairro && !data.some(b => Number(b.value) === selectedBairro)) {
          setSelectedBairro(null);
          setValue("endereco.bairro_nome", "");
        }
      });
    } else {
      setBairros([]);
      setSelectedBairro(null);
      setValue("endereco.bairro_nome", "");
    }
  }, [selectedCidade, setValue]);

  // Efeito para preencher os seletores iniciais no modo de edição
  useEffect(() => {
    if (initialData?.endereco) {
      const initialUfId = initialData.endereco.logradouro.cidade.uf?.id || null;
      const initialUfSigla = initialData.endereco.logradouro.cidade.uf?.sigla || "";
      const initialCidadeId = initialData.endereco.logradouro.cidade?.id || null;
      const initialCidadeName = initialData.endereco.logradouro.cidade?.nome_original || initialData.endereco.logradouro.cidade?.nome || "";
      const initialBairroId = initialData.endereco.logradouro.bairro?.id || null;
      const initialBairroName = initialData.endereco.logradouro.bairro?.nome_original || initialData.endereco.logradouro.bairro?.nome || "";
  
      setSelectedUfId(initialUfId);
      setSelectedCidade(initialCidadeId);
      setSelectedBairro(initialBairroId);
      
      setValue("endereco.uf_sigla", initialUfSigla);
      setValue("endereco.cidade_nome", initialCidadeName);
      setValue("endereco.bairro_nome", initialBairroName);
    } else {
      setSelectedUfId(null);
      setSelectedCidade(null);
      setSelectedBairro(null);
      setValue("endereco.uf_sigla", "");
      setValue("endereco.cidade_nome", "");
      setValue("endereco.bairro_nome", "");
    }
  }, [initialData, setValue]);


  // Limpa mensagem de erro quando usuário começa a editar campos
  useEffect(() => {
    if (errorMessage) {
      const subscription = watch(() => {
        setErrorMessage(null);
      });
      return () => subscription.unsubscribe();
    }
  }, [errorMessage, watch]);

  const tipo = watch("tipo");

  // Função auxiliar para comparar valores (deep comparison básica)
  const hasChanged = (field: string, currentValue: any): boolean => {
    if (!isEdit || !initialValuesRef.current) return true;

    const keys = field.split(".");
    let initialValue = initialValuesRef.current;

    for (const key of keys) {
      if (initialValue === undefined || initialValue === null) return true;
      initialValue = initialValue[key];
    }

    // Compara valores, tratando strings vazias e null/undefined como equivalentes
    const normalize = (val: any) => {
      if (val === null || val === undefined || val === "") return null;
      return val;
    };

    return normalize(currentValue) !== normalize(initialValue);
  };

  // Função para verificar se QUALQUER campo do endereço foi modificado
  const enderecoHasChanged = (enderecoData: any): boolean => {
    if (!isEdit || !initialValuesRef.current?.endereco) return false;

    // ✅ FIX: Só considerar mudança se usuário desbloqueou edição de endereço
    if (!isEditingEndereco) return false;

    return (
      hasChanged("endereco.zipcode", enderecoData.zipcode) ||
      hasChanged("endereco.nome", enderecoData.nome) ||
      hasChanged("endereco.numero", enderecoData.numero) ||
      hasChanged("endereco.complemento", enderecoData.complemento) ||
      hasChanged("endereco.uf_sigla", enderecoData.uf_sigla) ||
      hasChanged("endereco.cidade_nome", enderecoData.cidade_nome) ||
      hasChanged("endereco.bairro_nome", enderecoData.bairro_nome)
    );
  };

  const handleFormSubmit = (data: EmpresaCreate | EmpresaUpdate) => {
    // Limpa mensagem de erro anterior
    setErrorMessage(null);

    let dataToSubmit: any = {};

    // Se estiver em modo de edição, enviar apenas campos alterados
    if (isEdit && initialValuesRef.current) {
      // Verifica campos de nível superior
      if (hasChanged("razao_social", data.razao_social)) {
        dataToSubmit.razao_social = data.razao_social;
      }
      if (hasChanged("nome_fantasia", data.nome_fantasia)) {
        dataToSubmit.nome_fantasia = data.nome_fantasia;
      }
      if (hasChanged("cnpj", data.cnpj)) {
        dataToSubmit.cnpj = data.cnpj;
      }
      if (hasChanged("inscricao_municipal", data.inscricao_municipal)) {
        dataToSubmit.inscricao_municipal = data.inscricao_municipal;
      }
      if (hasChanged("inscricao_estadual", data.inscricao_estadual)) {
        dataToSubmit.inscricao_estadual = data.inscricao_estadual;
      }

      // Campos do admin - só incluir se foram alterados
      if (hasChanged("admin_email", data.admin_email)) {
        dataToSubmit.admin_email = data.admin_email;
      }
      if (hasChanged("admin_nome_completo", data.admin_nome_completo)) {
        dataToSubmit.admin_nome_completo = data.admin_nome_completo;
      }
      // Telefone só é enviado se foi preenchido (pois começa vazio na edição)
      if (data.admin_telefone && data.admin_telefone.trim() !== "") {
        dataToSubmit.admin_telefone = data.admin_telefone;
      }

      // Verifica mudanças no endereço
      const enderecoData = data.endereco as any;
      const enderecoChanged = enderecoData
        ? enderecoHasChanged(enderecoData)
        : false;

      if (enderecoChanged) {
        // 🔒 Verifica permissão: apenas TACT pode alterar endereço
        if (!isTactUser) {
          setErrorMessage(
            "🔒 Permissão Negada: Apenas usuários TACT podem alterar o endereço. " +
              "Usuários de Franquia só podem editar campos básicos (nome, razão social, inscrições)."
          );
          setTimeout(() => {
            errorBannerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 100);
          return;
        }

        // ⚠️ IMPORTANTE: Backend exige TODOS os campos do endereço quando atualiza
        // Validação: se mudou algo no endereço, todos os campos são obrigatórios
        const camposObrigatorios = [
          "zipcode",
          "nome",
          "numero",
          "uf_sigla",
          "cidade_nome",
          "bairro_nome",
        ];
        const camposFaltando = camposObrigatorios.filter((campo) => {
          const valor = enderecoData[campo];
          return (
            valor === null ||
            valor === undefined ||
            valor === "" ||
            (campo === "numero" && valor === 0)
          );
        });

        if (camposFaltando.length > 0) {
          const nomesCampos = {
            zipcode: "CEP",
            nome: "Logradouro",
            numero: "Número",
            uf_sigla: "Estado",
            cidade_nome: "Cidade",
            bairro_nome: "Bairro",
          };
          const listaCampos = camposFaltando
            .map((c) => nomesCampos[c as keyof typeof nomesCampos])
            .join(", ");
          setErrorMessage(
            `Para atualizar o endereço, todos os campos são obrigatórios. Campos faltando: ${listaCampos}`
          );
          // Scroll para o banner de erro
          setTimeout(() => {
            errorBannerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 100);
          return;
        }

        // Se passou na validação, envia o endereço completo no formato esperado
        dataToSubmit.endereco = {
          cep: enderecoData.zipcode,
          logradouro_nome: enderecoData.nome,
          numero: enderecoData.numero,
          complemento: enderecoData.complemento ?? "",
          bairro_nome: enderecoData.bairro_nome,
          municipio_nome: enderecoData.cidade_nome,
          uf_sigla: enderecoData.uf_sigla,
        };
      }

      // Se nenhum campo foi alterado, não enviar nada
      if (Object.keys(dataToSubmit).length === 0) {
        setErrorMessage(
          "Nenhuma alteração foi detectada. Modifique pelo menos um campo antes de salvar."
        );
        setTimeout(() => {
          errorBannerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
        return;
      }
    } else {
      // Modo de criação: enviar todos os dados
      dataToSubmit = { ...data };

      // Ajusta mapeamento dos campos de endereço para o formato esperado pela API
      if (dataToSubmit.endereco) {
        const e = dataToSubmit.endereco as any;
        dataToSubmit.endereco = {
          cep: e.zipcode ?? e.cep ?? "",
          logradouro_nome: e.nome ?? e.logradouro_nome ?? "",
          numero: e.numero ?? null,
          complemento: e.complemento ?? "",
          bairro_nome: e.bairro_nome ?? "",
          municipio_nome: e.cidade_nome ?? e.municipio_nome ?? "",
          uf_sigla: e.uf_sigla ?? "",
        };
      }

      if (dataToSubmit.tipo === "PF") {
        delete (dataToSubmit as Partial<EmpresaCreate>).razao_social;
        delete (dataToSubmit as Partial<EmpresaCreate>).nome_fantasia;
        delete (dataToSubmit as Partial<EmpresaCreate>).cnpj;
        delete (dataToSubmit as Partial<EmpresaCreate>).inscricao_municipal;
        delete (dataToSubmit as Partial<EmpresaCreate>).inscricao_estadual;
      }
    }

    onSubmit(dataToSubmit);
  };
  
  const ufOptions = ufs.map(uf => ({ value: uf.id, label: uf.nome, sigla: uf.sigla }));

  const planoOptions = planos.map((plano) => ({
    value: plano.id,
    label: plano.nome,
  }));

  const selectedPlano = planos.find((plano) => plano.id === selectedPlanoId);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Banner de erro/validação */}
      {errorMessage && (
        <div
          ref={errorBannerRef}
          className="p-4 bg-red-50 border border-red-200 rounded-lg animate-pulse"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-800 mb-1">
                Atenção: Validação Necessária
              </h4>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="text-red-600 hover:text-red-800 text-xl leading-none flex-shrink-0"
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Seletor de Planos */}
      {!isEdit && ( // Apenas em modo de criação
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-3">
            Escolha o Plano do Assinante
          </h3>
          {loadingPlans ? (
            <p className="text-gray-500">Carregando planos...</p>
          ) : planos.length === 0 ? (
            <p className="text-red-500">Nenhum plano disponível.</p>
          ) : (
            <>
              <Select
                options={planoOptions}
                value={planoOptions.find((opt) => opt.value === selectedPlanoId)}
                onChange={(selectedOption) => {
                  setSelectedPlanoId(selectedOption ? selectedOption.value : null);
                }}
                placeholder="Selecione um Plano"
                className="mt-1"
                isDisabled={loadingPlans}
              />

              {selectedPlano && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-md font-bold text-blue-800 mb-2">
                    Detalhes do Plano: {selectedPlano.nome}
                  </h4>
                  <p className="text-sm text-blue-700 mb-2">
                    <span className="font-semibold">Descrição:</span>{" "}
                    {selectedPlano.descricao}
                  </p>
                  <p className="text-sm text-blue-700 mb-2">
                    <span className="font-semibold">Valor:</span> R${" "}
                    {parseFloat(selectedPlano.valor).toFixed(2).replace(".", ",")}
                  </p>
                  <p className="text-sm text-blue-700 mb-2">
                    <span className="font-semibold">Máx. Membros:</span>{" "}
                    {selectedPlano.max_membros}
                  </p>
                  <p className="text-sm text-blue-700 mb-2">
                    <span className="font-semibold">Cidades Inclusas:</span>{" "}
                    {selectedPlano.quantidade_cidade}
                  </p>
                  <p className="text-sm text-blue-700 mb-2">
                    <span className="font-semibold">Segmentos Inclusos:</span>{" "}
                    {selectedPlano.quantidade_segmento}
                  </p>
                  {selectedPlano.acessos.length > 0 && (
                    <div className="mt-2">
                      <span className="font-semibold text-blue-800">Acessos:</span>
                      <ul className="list-disc list-inside text-sm text-blue-700">
                        {selectedPlano.acessos.map((acesso, index) => (
                          <li key={index}>{AcessoChoiceLabels[acesso] || acesso}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Tipo de Assinante
        </label>
        <select
          {...register("tipo")}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          disabled={isEdit}
        >
          <option value="PJ">Pessoa Jurídica</option>
          <option value="PF">Pessoa Física</option>
        </select>
      </div>

      {tipo === "PJ" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Razão Social
            </label>
            <input
              {...register("razao_social", { required: tipo === "PJ" })}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.razao_social && (
              <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nome Fantasia
            </label>
            <input
              {...register("nome_fantasia")}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              CNPJ
            </label>
            <input
              {...register("cnpj", { required: tipo === "PJ" })}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.cnpj && (
              <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Inscrição Municipal
            </label>
            <input
              {...register("inscricao_municipal")}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Inscrição Estadual
            </label>
            <input
              {...register("inscricao_estadual")}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </>
      )}

      <h3 className="text-lg font-semibold border-t pt-4">
        Dados do Administrador
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Nome Completo do Admin
        </label>
        <input
          {...register("admin_nome_completo", { required: true })}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        />
        {errors.admin_nome_completo && (
          <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Email do Admin
        </label>
        <input
          type="email"
          {...register("admin_email", { required: true })}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        />
        {errors.admin_email && (
          <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Telefone do Admin{" "}
          {isEdit && (
            <span className="text-gray-500 text-xs">
              (opcional - preencha apenas se desejar alterar)
            </span>
          )}
        </label>
        <input
          {...register("admin_telefone", { required: !isEdit })}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          placeholder={
            isEdit ? "Deixe em branco para manter o valor atual" : ""
          }
        />
        {errors.admin_telefone && (
          <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
        )}
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Endereço</h3>
          {isEdit && (
            <div className="flex items-center gap-2">
              {!isTactUser && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  🔒 Somente TACT
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!isTactUser) {
                    setErrorMessage(
                      "🔒 Permissão Negada: Apenas usuários TACT podem alterar o endereço. " +
                        "Entre em contato com o administrador TACT se precisar mudar a localização."
                    );
                    setTimeout(() => {
                      errorBannerRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                    return;
                  }
                  setIsEditingEndereco(!isEditingEndereco);
                }}
                disabled={!isTactUser}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  !isTactUser
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    : isEditingEndereco
                    ? "bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200"
                    : "bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200"
                }`}
              >
                {isEditingEndereco ? (
                  <>
                    <span>🔓</span>
                    <span className="font-medium">Bloqueando Endereço</span>
                  </>
                ) : (
                  <>
                    <span>🔒</span>
                    <span className="font-medium">Alterar Endereço</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {isEdit && isEditingEndereco && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 text-xl">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800 mb-1">
                  Modo de Edição de Endereço Ativo
                </p>
                <p className="text-xs text-yellow-700">
                  Ao alterar o endereço,{" "}
                  <strong>todos os campos são obrigatórios</strong> (CEP,
                  Logradouro, Número, Estado, Cidade e Bairro). Se não preencher
                  todos, a atualização será bloqueada. Isso evita dados
                  incompletos no sistema.
                </p>
              </div>
            </div>
          </div>
        )}

        {isEdit && !isEditingEndereco && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 text-xl">ℹ️</span>
              <div className="flex-1">
                <p className="text-sm text-blue-700">
                  Os campos de endereço estão <strong>bloqueados</strong> para
                  evitar alterações acidentais.
                  {isTactUser ? (
                    <>
                      {" "}
                      Clique em <strong>“Alterar Endereço”</strong> se a empresa
                      mudou de localização.
                    </>
                  ) : (
                    <>
                      {" "}
                      <strong className="text-blue-800">
                        🔒 Apenas usuários TACT
                      </strong>{" "}
                      podem alterar o endereço. Entre em contato com o
                      administrador se necessário.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">CEP</label>
          <input
            {...register("endereco.zipcode", { required: true })}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isEdit && !isEditingEndereco}
          />
          {errors.endereco?.zipcode && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Logradouro
          </label>
          <input
            {...register("endereco.nome", { required: true })}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isEdit && !isEditingEndereco}
          />
          {errors.endereco?.nome && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Número
          </label>
          <input
            type="number"
            {...register("endereco.numero", {
              required: true,
              valueAsNumber: true,
            })}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isEdit && !isEditingEndereco}
          />
          {errors.endereco?.numero && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Complemento
          </label>
          <input
            {...register("endereco.complemento")}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isEdit && !isEditingEndereco}
          />
        </div>
        {/* --- CAMPOS DE ENDEREÇO EM CASCATA --- */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Estado (UF)
          </label>
          <Select
            options={ufOptions}
            value={ufOptions.find(opt => opt.value === selectedUfId)}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSelectedUfId(selectedOption.value);
                setValue("endereco.uf_sigla", selectedOption.sigla);
                setSelectedCidade(null);
                setValue("endereco.cidade_nome", "");
                setSelectedBairro(null);
                setValue("endereco.bairro_nome", "");
              }
            }}
            placeholder="Selecione o Estado"
            isDisabled={isEdit && !isEditingEndereco}
          />
          {errors.endereco?.uf_sigla && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Cidade
          </label>
          <Select
            options={cidades}
            value={cidades.find(opt => Number(opt.value) === selectedCidade)}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSelectedCidade(Number(selectedOption.value));
                setValue("endereco.cidade_nome", selectedOption.label);
                setSelectedBairro(null);
                setValue("endereco.bairro_nome", "");
              }
            }}
            placeholder="Selecione a Cidade"
            isDisabled={!selectedUfId || (isEdit && !isEditingEndereco)}
          />
          {errors.endereco?.cidade_nome && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Bairro
          </label>
          <Select
            options={bairros}
            value={bairros.find(opt => Number(opt.value) === selectedBairro)}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSelectedBairro(Number(selectedOption.value));
                setValue("endereco.bairro_nome", selectedOption.label);
              }
            }}
            placeholder="Selecione o Bairro"
            isDisabled={!selectedCidade || (isEdit && !isEditingEndereco)}
          />
          {errors.endereco?.bairro_nome && (
            <p className="text-red-500 text-xs mt-1">Campo obrigatório.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {loading
            ? "Salvando..."
            : isEdit
            ? "Salvar Alterações"
            : "Criar Assinante"}
        </button>
      </div>
    </form>
  );
};
